import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useFileViewerContext } from 'js/file_viewer/contexts/file_viewer';
import { makeRivieraDataQueryPackage } from 'js/file_viewer/core/queries/riviera_base';
import { useSharedRivieraMutation } from 'js/file_viewer/core/queries/useSharedRivieraMutation';
import { rivieraClient } from 'js/file_viewer/core/riviera/api';
import { TranscriptRivieraError } from 'js/file_viewer/core/riviera/types';
import { PreviewsEventLogger, PreviewsTimingEvents } from 'js/file_viewer/logging/performance';
const fetchWaveformData = async (args) => {
    var _a;
    const content = (_a = args.previewMetadata) === null || _a === void 0 ? void 0 : _a.content;
    if ((content === null || content === void 0 ? void 0 : content['.tag']) !== 'audio' || !content.waveform_url) {
        return undefined;
    }
    const result = await rivieraClient.fetchWaveformData(content.waveform_url);
    if (result.error) {
        throw result.error;
    }
    return result.data;
};
const fetchMediaMetadata = async (args) => {
    var _a;
    const content = (_a = args.previewMetadata) === null || _a === void 0 ? void 0 : _a.content;
    if ((content === null || content === void 0 ? void 0 : content['.tag']) !== 'video' || !content.metadata_url) {
        return undefined;
    }
    const result = await rivieraClient.fetchMediaMetadata(content.metadata_url);
    if (result.error) {
        throw result.error;
    }
    return result.data;
};
export const prefetchAudioVideoRivieraData = (queryClient, previewKey, previewMetadata, preloadVideojs) => {
    if (!previewKey ||
        !previewMetadata.content ||
        (previewMetadata.content['.tag'] !== 'video' && previewMetadata.content['.tag'] !== 'audio')) {
        return Promise.resolve();
    }
    const prefetches = [];
    if (previewMetadata.content['.tag'] === 'video') {
        prefetches.push(metadataPackage.prefetchQuery(queryClient, {
            apiArg: {
                previewKey,
                previewMetadata,
            },
            pkgArg: null,
        }));
    }
    if (previewMetadata.content['.tag'] === 'audio') {
        prefetches.push(waveformPackage.prefetchQuery(queryClient, {
            apiArg: {
                previewKey,
                previewMetadata,
            },
            pkgArg: null,
        }));
    }
    if (preloadVideojs) {
        masterPlaylistPackage.prefetchQuery(queryClient, {
            apiArg: {
                previewKey,
                previewMetadata,
            },
            pkgArg: null,
        });
    }
    return Promise.all(prefetches);
};
const metadataPackage = makeRivieraDataQueryPackage({
    fileTypeSpecificQueryFn: fetchMediaMetadata,
    fieldName: 'media_metadata',
});
export const useMediaMetadataQuery = (previewKey, previewMetadata) => {
    const content = previewMetadata === null || previewMetadata === void 0 ? void 0 : previewMetadata.content;
    const isVideo = (content === null || content === void 0 ? void 0 : content['.tag']) === 'video';
    return metadataPackage.useQuery({
        apiArg: {
            previewKey,
            previewMetadata,
        },
        pkgArg: null,
    }, { enabled: !!previewKey && !!previewMetadata && isVideo });
};
const waveformPackage = makeRivieraDataQueryPackage({
    fileTypeSpecificQueryFn: fetchWaveformData,
    fieldName: 'waveform_data',
});
export const useWaveformDataQuery = (previewKey, previewMetadata) => {
    const content = previewMetadata === null || previewMetadata === void 0 ? void 0 : previewMetadata.content;
    const isAudio = (content === null || content === void 0 ? void 0 : content['.tag']) === 'audio';
    return waveformPackage.useQuery({
        apiArg: {
            previewKey,
            previewMetadata,
        },
        pkgArg: null,
    }, { enabled: !!previewKey && !!previewMetadata && isAudio });
};
const fetchTranscriptData = async (args) => {
    var _a;
    const content = (_a = args.previewMetadata) === null || _a === void 0 ? void 0 : _a.content;
    if ((content === null || content === void 0 ? void 0 : content['.tag']) !== 'video' && (content === null || content === void 0 ? void 0 : content['.tag']) !== 'audio') {
        return undefined;
    }
    const transcriptUrl = content.transcript_url;
    if (!transcriptUrl) {
        return undefined;
    }
    const transcriptUrlObject = new URL(transcriptUrl);
    if (args.isTranscriptEditEnabled) {
        transcriptUrlObject.searchParams.append('is_transcript_edit_enabled', '1');
    }
    const result = await rivieraClient.fetchTranscriptData(transcriptUrlObject.toString());
    if (result.error) {
        throw result.error;
    }
    return {
        segments: result.data.segments || [],
        ...result.data,
    };
};
const transcriptPackage = makeRivieraDataQueryPackage({
    fileTypeSpecificQueryFn: fetchTranscriptData,
    fieldName: 'transcript_data',
});
export const useTranscriptDataQuery = (previewKey, previewMetadata) => {
    const { isLoggedIn } = useFileViewerContext();
    const content = previewMetadata === null || previewMetadata === void 0 ? void 0 : previewMetadata.content;
    // FIXME: This first atom references `previewKey`, but the second uses `currentPreviewKey` internally. While these
    // will always be the same in practice, it's still janky.
    const isAudioVideo = (content === null || content === void 0 ? void 0 : content['.tag']) === 'video' || (content === null || content === void 0 ? void 0 : content['.tag']) === 'audio';
    const apiArg = {
        previewKey,
        previewMetadata,
        isTranscriptEditEnabled: isLoggedIn,
    };
    return transcriptPackage.useQuery({
        apiArg,
        pkgArg: null,
    }, {
        enabled: !!previewKey && !!previewMetadata && isAudioVideo,
    });
};
const fetchMasterPlaylist = async (args) => {
    var _a;
    const content = (_a = args.previewMetadata) === null || _a === void 0 ? void 0 : _a.content;
    if (((content === null || content === void 0 ? void 0 : content['.tag']) !== 'video' && (content === null || content === void 0 ? void 0 : content['.tag']) !== 'audio') || !content.transcode_url) {
        return undefined;
    }
    const result = await rivieraClient.fetchMasterPlaylist(content.transcode_url);
    PreviewsEventLogger.mark(PreviewsTimingEvents.FVSDK_VIDEO_MASTER_PLAYLIST_LOADED);
    if (result.error) {
        throw result.error;
    }
    return result.data;
};
const masterPlaylistPackage = makeRivieraDataQueryPackage({
    fileTypeSpecificQueryFn: fetchMasterPlaylist,
    fieldName: 'hls_master_playlist',
});
/**
 * Starts generating a transcript for the video specified in `fileId`. This function does not return any transcript
 * data or a status.
 */
export const useMaybeGenerateTranscript = (previewKey, previewMetadata) => {
    const { optionalIOClients: { flowsClient }, isLoggedIn, } = useFileViewerContext();
    const queryClient = useQueryClient();
    // Returns true if the transcript was successfully generated, false if generation was not needed,
    // and throws if the transcript could not be generated or we anticipate that it will not be able to be generated.
    const mutationFn = React.useCallback(async ({ previewKey, previewMetadata, fileId, sharedLinkUrl }) => {
        var _a, _b, _c;
        if (!flowsClient || !fileId || !previewKey) {
            throw new TranscriptRivieraError('error_unknown');
        }
        if (!isLoggedIn) {
            throw new TranscriptRivieraError('insufficient_permissions');
        }
        if ((((_a = previewMetadata === null || previewMetadata === void 0 ? void 0 : previewMetadata.content) === null || _a === void 0 ? void 0 : _a['.tag']) === 'video' ||
            ((_b = previewMetadata === null || previewMetadata === void 0 ? void 0 : previewMetadata.content) === null || _b === void 0 ? void 0 : _b['.tag']) === 'audio') &&
            ((_c = previewMetadata.content.transcript_permission) === null || _c === void 0 ? void 0 : _c['.tag']) !== 'opted_in') {
            throw new TranscriptRivieraError('insufficient_permissions');
        }
        const TRANSCRIPT_TIMEOUT = 15 * 60;
        const flowsResult = await flowsClient.submitFileWorkflow({
            template: {
                sequence: [
                    {
                        workflow_action: {
                            '.tag': 'transcribe_media',
                            // TODO: Remove this argument after the workflow is updated to not require it.
                            language: 'default',
                        },
                    },
                ],
            },
            file_ids: [fileId],
            shared_link_url: sharedLinkUrl,
        }, TRANSCRIPT_TIMEOUT);
        if (flowsResult.status === 'success') {
            transcriptPackage.refetchQueries(queryClient, {
                previewKey,
                previewMetadata,
                isTranscriptEditEnabled: isLoggedIn,
            });
            return true;
        }
        else if (flowsResult.status === 'error') {
            transcriptPackage.refetchQueries(queryClient, {
                previewKey,
                previewMetadata,
                isTranscriptEditEnabled: isLoggedIn,
            });
            const error = new TranscriptRivieraError(flowsResult.errorReason || 'error_unknown');
            throw error;
        }
        // Timeout error
        throw new TranscriptRivieraError('error_unknown');
    }, [flowsClient, isLoggedIn, queryClient]);
    return useSharedRivieraMutation(previewKey, previewMetadata, mutationFn);
};
//# sourceMappingURL=audio_video.preenvesnextjs.map
